<template>
  <div class="toggle__buttons">
    <div class="toggle-button" v-if="(props.items && props.items.length) || !$slots.empty">
      <div
        v-if="props.emptyItem"
        class="toggle-button__item"
        @click="select(props.emptyItem)"
        :class="{ 'toggle-button__item--active': isSelected(props.emptyItem) }"
      >
        <span>{{ props.emptyItem[props.textField] }}</span>
        <slot name="item" :value="props.emptyItem"/>
      </div>
      <div
        class="toggle-button__item"
        @click="select(row)"
        :class="{ 'toggle-button__item--active': isSelected(row) }"
        v-for="(row) in filteredItems"
        :key="row.id"
      >
        <span class="toggle-button__item-content">
          <span v-if="!$slots.item">{{ row[props.textField] }}</span>
          <slot name="item" :value="row"/>
        </span>
      </div>
      <div v-if="hasHiddenItems" class="toggle-button__item" @click="showItemModal">
        <span>{{ props.selectOtherText }}</span>
      </div>
    </div>
    <slot name="empty" v-else-if="$slots.empty"/>
    <s-modal :title="props.modalTitle" v-model="isItemModalVisible" v-if="!$slots.modal">
      <slot name="modalContent" :value="{ items: props.items, select, isSelected }"/>
      <template v-if="!$slots.modalContent">
        <s-form-group :key="row.id" v-for="(row) in items">
          <s-checkbox
            :model-value="isSelected(row)"
            class="mr-1 og-checkbox__radio"
            @click="select(row, true)"
          >
            <template v-if="!$slots.item">{{ row[props.textField] }}</template>
            <slot name="item" :value="row"/>
          </s-checkbox>
        </s-form-group>
      </template>
    </s-modal>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, toRef } from 'vue';

  const emit = defineEmits(['openModal', 'update:modelValue']);

  const props = defineProps({
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    modelValue: {
      type: null,
      required: true,
    },
    textField: {
      type: String,
      default: 'title',
    },
    keyField: {
      type: String,
      default: 'id',
    },
    maxItems: {
      type: null,
      default: null,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      required: false,
      default: null,
    },
    emptyItem: {
      type: null,
      default: null,
    },
    selectOtherText: {
      type: String,
      default: '+',
    },
    matchBy: {
      type: String,
      default: null,
    },
  });

  const isItemModalVisible = ref(false);
  const sortedItems = toRef(props, 'items');

  const filteredItems = computed(() => {
    if (sortedItems.value) {
      return sortedItems.value.filter(el => el !== undefined);
    }

    return [];
  });

  const hasHiddenItems = computed(() => {
    if (props.maxItems && filteredItems.value.length <= props.maxItems) {
      return true;
    }

    return filteredItems.value.length < sortedItems.value?.length;
  });

  const modelKey = computed(() => props.matchBy ? props.modelValue[props.matchBy] : props.modelValue);

  onMounted(() => {
    if (props.modelValue && props.maxItems) {
      const currentRow = props.items.find(it => it[props.keyField] === modelKey.value);
      sortedItems.value = props.items.filter(it => it[props.keyField] !== modelKey.value);
      sortedItems.value.unshift(currentRow);
    } else {
      sortedItems.value = props.items;
    }
  });


  function isSelected(row) {
    if (!props.modelValue || !row) {
      return false;
    }

    return row[props.keyField] === modelKey.value;
  }

  function select(row, unshift = false) {
    if (isSelected(row)) {
      if (props.nullable) {
        emit('update:modelValue', null);
      }
      return;
    }

    if (props.matchBy) {
      emit('update:modelValue', row);
    } else {
      emit('update:modelValue', row[props.keyField]);
    }

    if (unshift) {
      sortedItems.value = props.items.filter(it => it[props.keyField] !== row[props.keyField]);
      sortedItems.value.unshift(row);
    }
  }

  function showItemModal() {
    isItemModalVisible.value = true;
  }
</script>
